import { useEffect, useState } from "react";
import { getFollowers, assignFollowerToCoach } from "../../../services/crmTwitter";
import { TablePagination } from "../../../components/TablePagination";
import { AddContactModal } from "../contacts/AddContactModal";
import type { TwitterFollowerResponse } from "../../../types/crm";
import {
  followersScrollKey,
  followersViewKey,
  loadCrmViewState,
  readPageParam,
  saveCrmViewState,
} from "../utils/crmViewState";
import { useCrmScrollRestoration } from "../hooks/useCrmScrollRestoration";
import "./FollowersTabPage.css";

const PAGE_SIZE = 20;

const readStoredPage = (): number =>
  readPageParam(loadCrmViewState(followersViewKey())?.get("page") ?? null);

export const FollowersTabPage = () => {
  const [followers, setFollowers] = useState<TwitterFollowerResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(readStoredPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFollower, setSelectedFollower] =
    useState<TwitterFollowerResponse | null>(null);

  useEffect(() => {
    loadFollowers(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set("page", String(currentPage));
    saveCrmViewState(followersViewKey(), params);
  }, [currentPage]);

  useCrmScrollRestoration(followersScrollKey(), !loading && followers.length > 0);

  const loadFollowers = async (page: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await getFollowers(undefined, page, PAGE_SIZE);
      setFollowers(data.items);
      setTotalCount(data.totalCount);
    } catch {
      setError("Failed to load followers.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCrm = (follower: TwitterFollowerResponse) => {
    setSelectedFollower(follower);
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setSelectedFollower(null);
  };

  const handleCoachCreated = async (createdId?: string) => {
    if (createdId && selectedFollower) {
      try {
        await assignFollowerToCoach(selectedFollower.id, {
          coachId: createdId,
        });
      } catch {
        // Coach was created but linking failed — still close modal and refresh
      }
    }
    setShowAddModal(false);
    setSelectedFollower(null);
    loadFollowers(currentPage);
  };

  return (
    <div className="followers-tab">
      <div className="followers-tab__header">
        <h2 className="followers-tab__title">X Followers</h2>
        <p className="followers-tab__subtitle">
          Accounts that follow your connected X profile.
          {totalCount > 0 && ` ${totalCount} total.`}
        </p>
      </div>

      {error && <div className="followers-tab__error">{error}</div>}

      {loading && currentPage === 1 ? (
        <div className="followers-tab__loading">Loading followers...</div>
      ) : followers.length === 0 && !loading ? (
        <div className="followers-tab__empty">
          No followers yet. New followers will appear here automatically.
        </div>
      ) : (
        <>
          <div className="followers-tab__list">
            {followers.map((follower) => (
              <div key={follower.id} className="followers-tab__item">
                <div className="followers-tab__info">
                  <span className="followers-tab__handle">
                    @{follower.twitterHandle}
                  </span>
                  {follower.displayName && (
                    <span className="followers-tab__name">
                      {follower.displayName}
                    </span>
                  )}
                  {follower.bio && (
                    <span className="followers-tab__bio">{follower.bio}</span>
                  )}
                </div>
                <div className="followers-tab__meta">
                  {follower.linkedCoachId ? (
                    <span className="followers-tab__linked">Linked</span>
                  ) : (
                    <button
                      className="followers-tab__add-btn"
                      onClick={() => handleAddToCrm(follower)}
                    >
                      Add to CRM
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <TablePagination
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {showAddModal && selectedFollower && (() => {
        const nameParts = (selectedFollower.displayName ?? "").trim().split(/\s+/);
        const initialFirstName = nameParts[0] || "";
        const initialLastName = nameParts.slice(1).join(" ") || "";
        return (
          <AddContactModal
            isOpen={showAddModal}
            contactType="Coach"
            onClose={handleModalClose}
            onSuccess={handleCoachCreated}
            initialXHandle={selectedFollower.twitterHandle}
            initialFirstName={initialFirstName}
            initialLastName={initialLastName}
          />
        );
      })()}
    </div>
  );
};
